import { PersonBbox } from '../interfaces/image-album.interface';

export interface FaceRelativeBbox {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

export interface FaceWithRelativeBbox {
  readonly relativeBbox: FaceRelativeBbox;
}

export interface BleedInset {
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly left: number;
}

export interface ImageRect {
  width: number;
  height: number;
  left: number;
  top: number;
}

export interface ContainerRect {
  width: number;
  height: number;
}

export interface FaceBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FaceCenteringResult {
  shouldAdjust: boolean;
  offsetX: number;
  offsetY: number;
  optimalZoom: number;
}

const MAX_FACE_CENTERING_ZOOM = 1.1;
const ZOOM_TRIGGER_THRESHOLD = 2;
const HORIZONTAL_CENTERING_FACTOR = 0.5;
// Face-bbox детектора начинается от волос (проверено на реальных фото): запас над ним —
// малый страховочный, не оценка причёски. Больше — двойной счёт волос и мёртвый воздух сверху.
// Экспортируется, потому что серверная сборка книги считает воздух над головой той же долей.
export const ESTIMATED_CROWN_FACTOR = 0.05;
// Верх personBbox выше капа над лицом (поднятая рука, предмет в том же боксе) макушкой не считается.
// Долю даёт САМОЕ ВЕРХНЕЕ лицо: union нескольких лиц высок и раздувает кап.
const MAX_CROWN_EXTENSION_FACTOR = 0.75;
// Целевой воздух над макушкой — доля высоты кадра. Не минимум: столько и оставляем, остальное отдаём телу.
const CROWN_HEAD_AIR_FACTOR = 0.02;
// Фигура влезла целиком — эта доля свободного остатка уходит над макушкой, остальное под фигуру.
const HEADROOM_SLACK_SHARE = 0.1;
// Под подбородком всегда шея: доля высоты лица, но не больше доли кадра. Кап 0.09 снят с ручного
// кропа фотографа: при крупном портрете место отдаётся лбу, а не шее.
const NECK_AIR_FACTOR = 0.25;
const MAX_NECK_AIR_FACTOR = 0.09;
// Доля кадра, которую должна занимать фигура; мельче — приближаем, но не сильнее ни этой константы,
// ни потолка вызывающего: он знает, сколько кропа его слот терпит.
const SUBJECT_FILL_TARGET = 0.85;
const MAX_SUBJECT_FILL_ZOOM = 1.25;

interface SafeArea {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly centerX: number;
  readonly centerY: number;
}

/** Геометрия сюжета в пикселях невзумленного изображения. */
interface SubjectMetrics {
  readonly person: PersonBbox | null;
  readonly faceLeft: number;
  readonly faceRight: number;
  readonly faceTop: number;
  readonly faceBottom: number;
  readonly faceHeight: number;
  /** Самое высокое отдельное лицо: общий bbox нескольких лиц под мерку шеи и обрезки не годится. */
  readonly tallestFaceHeight: number;
  /** Высота самого верхнего лица — от него оцениваются макушка и кап над ней. */
  readonly topFaceHeight: number;
  readonly faceCenterX: number;
  readonly crownTop: number;
  readonly personBottom: number;
  readonly personCenterX: number;
  readonly personWidth: number;
  readonly personHeight: number;
  readonly subjectCenterY: number;
  /** Часть лиц пожертвована (группа шире рамки): кадр центрируется на вошедших. */
  readonly facesSacrificed: boolean;
}

export function calculateFaceCenteredPosition(
  faces: FaceWithRelativeBbox[],
  originalImageWidth: number,
  originalImageHeight: number,
  container: ContainerRect,
  image: ImageRect,
  maxZoom: number = MAX_FACE_CENTERING_ZOOM,
  personBboxes?: PersonBbox[],
  bleedInset?: BleedInset,
): FaceCenteringResult {
  const validFaces = (faces ?? []).filter(isFaceWithRelativeBbox);
  if (validFaces.length === 0) {
    return { shouldAdjust: false, offsetX: 0, offsetY: 0, optimalZoom: 1 };
  }

  const scaledFaces = validFaces.map(f =>
    convertToScaledBbox(faceToRelativeBbox(f), originalImageWidth, originalImageHeight, image),
  );

  // Лицо крупнее рамки не урезается до её размера: кадр решают якоря (шея снизу важнее макушки сверху).
  return calculateOffset(scaledFaces, container, image, maxZoom, personBboxes, bleedInset);
}

function isFaceWithRelativeBbox(face: FaceWithRelativeBbox | null | undefined): face is FaceWithRelativeBbox {
  const b = face?.relativeBbox;
  return !!b && typeof b.x1 === 'number' && typeof b.y1 === 'number' && typeof b.x2 === 'number' && typeof b.y2 === 'number';
}

function faceToRelativeBbox(face: FaceWithRelativeBbox): FaceBoundingBox {
  return {
    x: face.relativeBbox.x1,
    y: face.relativeBbox.y1,
    width: face.relativeBbox.x2 - face.relativeBbox.x1,
    height: face.relativeBbox.y2 - face.relativeBbox.y1,
  };
}

function convertToScaledBbox(
  relativeBbox: FaceBoundingBox,
  imageWidth: number,
  imageHeight: number,
  image: ImageRect,
): FaceBoundingBox {
  const scaleX = image.width / imageWidth;
  const scaleY = image.height / imageHeight;

  return {
    x: relativeBbox.x * imageWidth * scaleX,
    y: relativeBbox.y * imageHeight * scaleY,
    width: relativeBbox.width * imageWidth * scaleX,
    height: relativeBbox.height * imageHeight * scaleY,
  };
}

/**
 * Кадрирование по приоритетам фотографа:
 * 1. лицо целиком, подбородок не режется; крупный портрет срезается по лбу, но запас
 *    внизу отдан лбу, а не шее (шея — не больше NECK-капа);
 * 2. макушка у верхнего края с минимальным воздухом (face-bbox уже включает волосы),
 *    весь остаток кадра достаётся телу, не фону;
 * 3. мелкая в кадре фигура приближается, чтобы занять кадр;
 * 4. группа шире рамки: в кадр — максимум целых лиц, не влезающие жертвуются,
 *    вошедшие распределяются по кадру равномерно;
 * 5. по горизонтали баланс держит фигура целиком (руки), но лицо остаётся в кадре.
 */
function calculateOffset(
  scaledFaces: FaceBoundingBox[],
  container: ContainerRect,
  image: ImageRect,
  maxZoom: number,
  personBboxes?: PersonBbox[],
  bleedInset?: BleedInset,
): FaceCenteringResult {
  const safe = resolveSafeArea(container, bleedInset);
  const includedFaces = selectFacesForWidth(scaledFaces, safe.width);
  const faceBbox = unionBbox(includedFaces);
  const matchingPerson = personBboxes?.length ? findMatchingPersonBbox(faceBbox, image, personBboxes) : null;
  const metrics = collectSubjectMetrics(
    faceBbox,
    image,
    matchingPerson,
    includedFaces,
    includedFaces.length < scaledFaces.length,
  );

  const ideal = resolveAnchors(metrics, 1, image, safe);
  const clamped = clampOffset(ideal.offsetX, ideal.offsetY, image, container);
  const zoom = resolveZoom(metrics, ideal, clamped, image, container, safe, maxZoom);

  if (zoom <= 1.001) {
    const shouldAdjust = Math.abs(clamped.offsetX) > 1 || Math.abs(clamped.offsetY) > 1;
    return { shouldAdjust, offsetX: clamped.offsetX, offsetY: clamped.offsetY, optimalZoom: 1 };
  }

  const zoomed: ImageRect = {
    width: image.width * zoom,
    height: image.height * zoom,
    left: image.left - (image.width * zoom - image.width) / 2,
    top: image.top - (image.height * zoom - image.height) / 2,
  };
  const zoomedIdeal = resolveAnchors(metrics, zoom, zoomed, safe);
  const zoomedClamped = clampOffset(zoomedIdeal.offsetX, zoomedIdeal.offsetY, zoomed, container);

  return { shouldAdjust: true, offsetX: zoomedClamped.offsetX, offsetY: zoomedClamped.offsetY, optimalZoom: zoom };
}

function resolveSafeArea(container: ContainerRect, bleedInset?: BleedInset): SafeArea {
  const left = bleedInset?.left ?? 0;
  const top = bleedInset?.top ?? 0;
  const width = container.width - left - (bleedInset?.right ?? 0);
  const height = container.height - top - (bleedInset?.bottom ?? 0);

  return { left, top, width, height, centerX: left + width / 2, centerY: top + height / 2 };
}

function collectSubjectMetrics(
  faceBbox: FaceBoundingBox,
  image: ImageRect,
  person: PersonBbox | null,
  includedFaces: FaceBoundingBox[],
  facesSacrificed: boolean,
): SubjectMetrics {
  const faceCenterX = faceBbox.x + faceBbox.width / 2;
  const tallestFaceHeight = Math.max(...includedFaces.map(f => f.height));
  const topFaceHeight = includedFaces.reduce((top, f) => (f.y < top.y ? f : top)).height;

  return {
    person,
    faceLeft: faceBbox.x,
    faceRight: faceBbox.x + faceBbox.width,
    faceTop: faceBbox.y,
    faceBottom: faceBbox.y + faceBbox.height,
    faceHeight: faceBbox.height,
    tallestFaceHeight,
    topFaceHeight,
    faceCenterX,
    crownTop: resolveCrownTopPx(faceBbox, topFaceHeight, image, person),
    personBottom: person ? (person.y + person.height) * image.height : 0,
    personCenterX: person ? (person.x + person.width / 2) * image.width : faceCenterX,
    personWidth: person ? person.width * image.width : 0,
    personHeight: person ? person.height * image.height : 0,
    subjectCenterY: estimateSubjectCenterY(faceBbox, image, person),
    facesSacrificed,
  };
}

/**
 * Баланс по горизонтали держит вся фигура (кисти рук), лицо — только страховка от обрезки.
 * Группа с пожертвованными лицами центрируется на вошедших — фигура тянула бы к отрезанным.
 * Одна точка для сдвига и для зума: разойдись они, зум считался бы под другую цель, чем сдвиг.
 */
function resolveHorizontalAnchor(m: SubjectMetrics): { centerX: number; pull: number } {
  const usePerson = !m.facesSacrificed && !!m.person;

  return {
    centerX: usePerson ? m.personCenterX : m.faceCenterX,
    pull: m.facesSacrificed || m.person ? 1 : HORIZONTAL_CENTERING_FACTOR,
  };
}

/** Идеальные (до клампа) сдвиги для изображения, уже увеличенного в `zoom` раз. */
function resolveAnchors(m: SubjectMetrics, zoom: number, rect: ImageRect, safe: SafeArea): { offsetX: number; offsetY: number } {
  const horizontal = resolveHorizontalAnchor(m);
  const anchorX = horizontal.centerX * zoom + rect.left;
  const pull = horizontal.pull;
  const faceLeft = m.faceLeft * zoom + rect.left;
  const faceRight = m.faceRight * zoom + rect.left;
  let offsetX = pull * (safe.centerX - anchorX);

  if (faceRight - faceLeft <= safe.width) {
    offsetX = Math.min(Math.max(offsetX, safe.left - faceLeft), safe.left + safe.width - faceRight);
  }

  const crownTop = m.crownTop * zoom + rect.top;
  const crownAir = CROWN_HEAD_AIR_FACTOR * safe.height;
  let offsetY: number;

  if (m.person) {
    // Макушка у верхнего края; если фигура влезла целиком — лишнее место делим в пользу тела.
    const subjectHeight = Math.max(0, (m.personBottom - m.crownTop) * zoom);
    const slack = Math.max(0, safe.height - crownAir - subjectHeight);
    offsetY = safe.top + crownAir + slack * HEADROOM_SLACK_SHARE - crownTop;
  } else {
    // Без бокса тела правило то же: макушка у верхнего края, остаток кадра — телу, не воздуху.
    offsetY = safe.top + crownAir - crownTop;
  }

  // Шея под подбородком сильнее воздуха над макушкой — кадр едет вверх, срезая волосы.
  // Но лицо, которое влезает в рамку, сверху не режется: там сдвиг останавливается на кромке лица.
  // Лицо выше самой рамки (совсем крупный портрет) целиком не показать — режем лоб, по бровям.
  const faceTopInFrame = m.faceTop * zoom + rect.top;
  const faceBottom = m.faceBottom * zoom + rect.top;
  const neckAir = Math.min(NECK_AIR_FACTOR * m.tallestFaceHeight * zoom, MAX_NECK_AIR_FACTOR * safe.height);
  const chinCap = safe.top + safe.height - neckAir - faceBottom;
  // Мерка — самое высокое ОДНО лицо, не их union: пока хоть одно лицо влезает, верх защищён,
  // и группе выше рамки достаётся ровно неизбежный срез подбородка, без запаса на шею сверх него.
  const faceFitsFrame = m.tallestFaceHeight * zoom <= safe.height;
  offsetY = Math.min(offsetY, faceFitsFrame ? Math.max(chinCap, safe.top - faceTopInFrame) : chinCap);

  return { offsetX, offsetY };
}

function resolveZoom(
  m: SubjectMetrics,
  ideal: { offsetX: number; offsetY: number },
  clamped: { offsetX: number; offsetY: number },
  image: ImageRect,
  container: ContainerRect,
  safe: SafeArea,
  maxZoom: number,
): number {
  if (maxZoom <= 1) return 1;

  const verticalSlack = image.height - container.height;
  const horizontalSlack = image.width - container.width;
  // Фото ровно по рамке — любой зум создал бы кроп, которого не было (FKS-8152).
  if (verticalSlack < 2 && horizontalSlack < 2) return 1;

  const fillZoom = Math.min(subjectFillZoom(m, safe), maxZoom);

  const lossX = Math.abs(ideal.offsetX - clamped.offsetX);
  const lossY = Math.abs(ideal.offsetY - clamped.offsetY);
  // Свободна одна ось: довести лицо до центра нечем, но приблизить мелкую фигуру можно. Гарантия
  // FKS-8152 «не создавать кроп там, где его не было» здесь сознательно ослаблена: по свободной оси
  // кроп уже есть, а по зажатой зум добирает его ради того, чтобы фигура заняла рамку.
  const centeringBlocked = verticalSlack < 2 || horizontalSlack < 2;

  if (centeringBlocked || (lossX <= ZOOM_TRIGGER_THRESHOLD && lossY <= ZOOM_TRIGGER_THRESHOLD)) return fillZoom;

  const horizontal = resolveHorizontalAnchor(m);
  const anchorX = horizontal.centerX + image.left;
  const dampenedDesiredX = anchorX + horizontal.pull * (safe.centerX - anchorX);
  const neededZoom = calculateNeededZoom(horizontal.centerX, m.subjectCenterY, dampenedDesiredX, safe.centerY, image, container);

  return Math.max(fillZoom, Math.min(neededZoom, maxZoom));
}

/** Фигура мельче кадра — приближаем, чтобы она его заняла (кисти и лицо страхуют якоря). */
function subjectFillZoom(m: SubjectMetrics, safe: SafeArea): number {
  if (!m.person || m.personWidth <= 0 || m.personHeight <= 0) return 1;

  const coverage = Math.max(m.personHeight / safe.height, m.personWidth / safe.width);
  if (coverage <= 0 || coverage >= SUBJECT_FILL_TARGET) return 1;

  return Math.min(SUBJECT_FILL_TARGET / coverage, MAX_SUBJECT_FILL_ZOOM);
}

function calculateNeededZoom(
  faceCenterX: number,
  faceCenterY: number,
  desiredX: number,
  desiredY: number,
  image: ImageRect,
  container: ContainerRect,
): number {
  let zoom = 1;

  if (faceCenterX > 0) {
    zoom = Math.max(zoom, desiredX / faceCenterX);
  }

  if (image.width - faceCenterX > 0) {
    zoom = Math.max(zoom, (container.width - desiredX) / (image.width - faceCenterX));
  }

  if (faceCenterY > 0) {
    zoom = Math.max(zoom, desiredY / faceCenterY);
  }

  if (image.height - faceCenterY > 0) {
    zoom = Math.max(zoom, (container.height - desiredY) / (image.height - faceCenterY));
  }

  return zoom;
}

const BODY_WEIGHT = 0.45;

function estimateSubjectCenterY(faceBbox: FaceBoundingBox, image: ImageRect, matchingPerson: PersonBbox | null): number {
  if (matchingPerson) {
    const personTop = matchingPerson.y * image.height;
    const personBottom = (matchingPerson.y + matchingPerson.height) * image.height;
    return (personTop + personBottom) / 2;
  }

  const faceBottom = faceBbox.y + faceBbox.height;
  const spaceBelow = image.height - faceBottom;
  const bodyContribution = spaceBelow * BODY_WEIGHT;
  const subjectBottom = faceBottom + bodyContribution;
  return (faceBbox.y + subjectBottom) / 2;
}

/** Верх макушки в px изображения: personBbox если он выше face-bbox (не дальше капа), иначе малый запас над верхним лицом. */
function resolveCrownTopPx(
  faceBbox: FaceBoundingBox,
  topFaceHeight: number,
  image: ImageRect,
  matchingPerson: PersonBbox | null,
): number {
  const estimated = faceBbox.y - ESTIMATED_CROWN_FACTOR * topFaceHeight;
  if (!matchingPerson) return estimated;
  const personTop = matchingPerson.y * image.height;
  if (personTop >= faceBbox.y) return estimated;
  return Math.max(personTop, faceBbox.y - MAX_CROWN_EXTENSION_FACTOR * topFaceHeight);
}

function findMatchingPersonBbox(faceBbox: FaceBoundingBox, image: ImageRect, personBboxes: PersonBbox[]): PersonBbox | null {
  const faceCenterXRel = (faceBbox.x + faceBbox.width / 2) / image.width;
  const faceCenterYRel = (faceBbox.y + faceBbox.height / 2) / image.height;

  const matching = personBboxes.filter(
    p => faceCenterXRel >= p.x && faceCenterXRel <= p.x + p.width && faceCenterYRel >= p.y && faceCenterYRel <= p.y + p.height,
  );

  if (matching.length === 0) return null;

  return matching.reduce((largest, p) => (p.width * p.height > largest.width * largest.height ? p : largest));
}

function clampOffset(
  offsetX: number,
  offsetY: number,
  image: ImageRect,
  container: ContainerRect,
): { offsetX: number; offsetY: number } {
  let clampedX = offsetX;
  let clampedY = offsetY;

  const newLeft = image.left + clampedX;
  const newTop = image.top + clampedY;

  const minLeft = container.width - image.width;
  const maxLeft = 0;
  const minTop = container.height - image.height;
  const maxTop = 0;

  if (newLeft < minLeft) clampedX = minLeft - image.left;
  else if (newLeft > maxLeft) clampedX = maxLeft - image.left;

  if (newTop < minTop) clampedY = minTop - image.top;
  else if (newTop > maxTop) clampedY = maxTop - image.top;

  return { offsetX: clampedX, offsetY: clampedY };
}

function unionBbox(boxes: FaceBoundingBox[]): FaceBoundingBox {
  if (boxes.length === 1) return boxes[0];

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const b of boxes) {
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.width);
    maxY = Math.max(maxY, b.y + b.height);
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/**
 * Группа шире рамки: цель фотографа — «поместить как можно больше лиц», не влезающие
 * жертвуются целиком. Выбирается подмножество лиц максимальной численности, чей union
 * помещается в ширину рамки; при равной численности — с большей суммарной площадью лиц
 * (крупный передний план важнее случайного прохожего в глубине), при равной площади —
 * более компактное (кадру достаётся больше тел).
 */
// ponytail: O(n²) перебор пар границ; лиц на фото единицы — быстрее не нужно.
function selectFacesForWidth(faces: FaceBoundingBox[], safeWidth: number): FaceBoundingBox[] {
  const union = unionBbox(faces);
  if (faces.length === 1 || union.width <= safeWidth) return faces;

  const sorted = [...faces].sort((a, b) => a.x - b.x);
  const areaOf = (boxes: FaceBoundingBox[]): number => boxes.reduce((sum, f) => sum + f.width * f.height, 0);
  // Площади после convertToScaledBbox — произведения чисел с плавающей точкой; математически
  // равные площади (симметричные лица) расходятся на ~1e-11 из-за накопленной погрешности.
  // Без допуска такая ничья решается в пользу случайного (более позднего по x) лица.
  const AREA_EPSILON = 1e-6;
  let best: FaceBoundingBox[] = [sorted[0]];
  let bestArea = areaOf(best);
  let bestWidth = sorted[0].width;

  for (let i = 0; i < sorted.length; i++) {
    const left = sorted[i].x;
    const subset = sorted.filter(f => f.x >= left && f.x + f.width <= left + safeWidth);
    if (subset.length === 0) continue;
    const subsetArea = areaOf(subset);
    const subsetWidth = unionBbox(subset).width;
    const wins =
      subset.length > best.length ||
      (subset.length === best.length &&
        (subsetArea > bestArea + AREA_EPSILON || (Math.abs(subsetArea - bestArea) <= AREA_EPSILON && subsetWidth < bestWidth)));
    if (wins) {
      best = subset;
      bestArea = subsetArea;
      bestWidth = subsetWidth;
    }
  }

  return best;
}

const TOP_BIAS_FRACTION = 0.1;
const PERSON_HEAD_AIR_FACTOR = 0.05;

function isPersonBbox(p: PersonBbox | null | undefined): p is PersonBbox {
  return !!p && typeof p.x === 'number' && typeof p.y === 'number' && typeof p.width === 'number' && typeof p.height === 'number';
}

function getCommonPersonBbox(persons: PersonBbox[]): FaceBoundingBox {
  if (persons.length === 1) {
    return { x: persons[0].x, y: persons[0].y, width: persons[0].width, height: persons[0].height };
  }

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const p of persons) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + p.width);
    maxY = Math.max(maxY, p.y + p.height);
  }

  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Body-bbox centering fallback when faces are absent; top of bbox ≈ head and is guarded against top-crop. */
export function calculatePersonCenteredPosition(
  personBboxes: PersonBbox[],
  container: ContainerRect,
  image: ImageRect,
  bleedInset?: BleedInset,
): FaceCenteringResult {
  const valid = (personBboxes ?? []).filter(isPersonBbox);
  if (valid.length === 0) {
    return { shouldAdjust: false, offsetX: 0, offsetY: 0, optimalZoom: 1 };
  }

  const bbox = getCommonPersonBbox(valid);
  const personTopPx = bbox.y * image.height;
  const personCenterX = (bbox.x + bbox.width / 2) * image.width;
  const personCenterY = (bbox.y + bbox.height / 2) * image.height;

  const safeLeft = bleedInset?.left ?? 0;
  const safeTop = bleedInset?.top ?? 0;
  const safeWidth = container.width - safeLeft - (bleedInset?.right ?? 0);
  const safeHeight = container.height - safeTop - (bleedInset?.bottom ?? 0);
  const desiredX = safeLeft + safeWidth / 2;
  const desiredY = safeTop + safeHeight / 2;

  const idealOffsetX = HORIZONTAL_CENTERING_FACTOR * (desiredX - (personCenterX + image.left));
  const rawOffsetY = desiredY - (personCenterY + image.top);

  const airAboveHead = container.height * PERSON_HEAD_AIR_FACTOR;
  const headTopInContainer = personTopPx + image.top;
  const minOffsetYForHead = airAboveHead - headTopInContainer;
  const idealOffsetY = minOffsetYForHead > 0 ? Math.max(rawOffsetY, minOffsetYForHead) : rawOffsetY;

  const clamped = clampOffset(idealOffsetX, idealOffsetY, image, container);
  const shouldAdjust = Math.abs(clamped.offsetX) > 1 || Math.abs(clamped.offsetY) > 1;

  return { shouldAdjust, offsetX: clamped.offsetX, offsetY: clamped.offsetY, optimalZoom: 1 };
}

/** Last-resort heuristic: bias a portrait's crop toward the top (head) when no face/bbox data, any frame shape. */
export function calculateTopBiasPosition(
  originalImageWidth: number,
  originalImageHeight: number,
  container: ContainerRect,
  image: ImageRect,
  bleedInset?: BleedInset,
): FaceCenteringResult {
  const verticalSlack = image.height - container.height;
  if (originalImageHeight <= originalImageWidth || verticalSlack <= 2) {
    return { shouldAdjust: false, offsetX: 0, offsetY: 0, optimalZoom: 1 };
  }

  const safeTop = bleedInset?.top ?? 0;
  const desiredTopCrop = verticalSlack * TOP_BIAS_FRACTION;
  const idealOffsetY = safeTop - desiredTopCrop - image.top;

  const clamped = clampOffset(0, idealOffsetY, image, container);
  const shouldAdjust = Math.abs(clamped.offsetY) > 1;

  return { shouldAdjust, offsetX: 0, offsetY: clamped.offsetY, optimalZoom: 1 };
}
